import React from 'react';

/**
 * PageContainer
 *
 * A shared layout wrapper for page-level content. Provides consistent
 * horizontal padding (critical for mobile), max-width constraints, and
 * centering across all pages.
 *
 * Usage:
 *   <PageContainer maxWidth={600}>...</PageContainer>
 *   <PageContainer maxWidth="wide">...</PageContainer>
 */

type MaxWidthPreset = 'narrow' | 'default' | 'wide';

interface PageContainerProps {
  /** Content to render inside the container */
  children: React.ReactNode;
  /**
   * Maximum width of the container.
   * - number: exact pixel value (e.g. 600)
   * - 'narrow': 600px (forms, single-column content)
   * - 'default': 1100px (tables, dashboards)
   * - 'wide': 1152px (large tables, comparison views)
   */
  maxWidth?: number | MaxWidthPreset;
  /** Additional inline styles */
  style?: React.CSSProperties;
  /** Additional CSS class names */
  className?: string;
}

const presetWidths: Record<MaxWidthPreset, number> = {
  narrow: 600,
  default: 1100,
  wide: 1152,
};

const PageContainer: React.FC<PageContainerProps> = ({
  children,
  maxWidth = 'default',
  style,
  className,
}) => {
  const resolvedMaxWidth =
    typeof maxWidth === 'number' ? maxWidth : presetWidths[maxWidth];

  return (
    <div
      className={className}
      style={{
        maxWidth: resolvedMaxWidth,
        margin: '0 auto',
        padding: '1rem',
        boxSizing: 'border-box',
        ...style,
      }}
    >
      {children}
    </div>
  );
};

export default PageContainer;
